/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import ApiMethods from "./ApiMethods";
import ENDPOINTS from "./EndPoint";

class APIManager {
  static getApplication = (applicationId) => {
    const url = ENDPOINTS.GET_APPLICATION(applicationId);
    return ApiMethods.getUrl(url);
  };
  static getCognitoUsers = (_userIds, queryParams = "") => {
    const url = ENDPOINTS.GET_COGNITO_USERS(queryParams);
    const payload = { status: ["ACTIVE"] };
    return ApiMethods.post(url, payload);
  };

static getDomainApproverAliasID = () => {
    const url = ENDPOINTS.GET_DOMAIN_APPROVER_ALIAS_ID();
    return ApiMethods.getUrl(url);
  };


  static addEcuTypeDomainApprover = (formData, makeName, ecuType) => {
    const url = ENDPOINTS.ADD_ECU_TYPE_DOMAIN_APPROVER(makeName, ecuType);
    return ApiMethods.post(url, formData);
  };

static deleteEcuTypeDomainApprover = (formData, makeName, ecuType) => {
    const url = ENDPOINTS.DELETE_ECU_TYPE_DOMAIN_APPROVER(makeName, ecuType);
    return ApiMethods.delete(url, formData);
  };

  static getGlobalAdminPublisherInfoCognito = (paymentID) => {
    const url = ENDPOINTS.GET_GLOBAL_ADMIN_PUBLISHER_INFO_COGNITO(paymentID);
    return ApiMethods.getUrl(url);
  };

  static getSubmission = (applicationId, submissionId) => {
    const url = ENDPOINTS.GET_SUBMISSION(applicationId, submissionId);
    return ApiMethods.getUrl(url);
  };

  static getSubmissionChangelog = (applicationId, submissionId) => {
    const url = ENDPOINTS.GET_SUBMISSION_CHANGELOG(applicationId, submissionId);
    return ApiMethods.getUrl(url);
  };

  static getAdminSubmissionChangelog = (applicationId, submissionId) => {
    const url = ENDPOINTS.GET_ADMIN_SUBMISSION_CHANGELOG(applicationId, submissionId);
    return ApiMethods.getUrl(url);
  };

  static getCategories = () => {
    const url = ENDPOINTS.GET_CATEGORIES();
    return ApiMethods.getUrl(url);
  };

  static postCreateCategory = (formData) => {
    const url = ENDPOINTS.POST_CREATE_CATEGORY();
    return ApiMethods.postFormData(url, formData);
  };

  static putUpdateCategory = (categoryId, data) => {
    const url = ENDPOINTS.PUT_UPDATE_CATEGORY(categoryId);
    if (data instanceof FormData) {
      return ApiMethods.putFormData(url, data);
    } else {
      return ApiMethods.put(url, data);
    }
  };

  static postCategoryIcon = (categoryId, file) => {
    const url = ENDPOINTS.POST_CATEGORY_ICON(categoryId);
    const formData = new FormData();
    formData.append("file", file);
    return ApiMethods.postFormData(url, formData);
  };

  static deleteCategory = (categoryId) => {
    const url = ENDPOINTS.DELETE_CATEGORY(categoryId);
    return ApiMethods.delete(url);
  };

  static getTeams = (teamId) => {
    const url = ENDPOINTS.GET_TEAMS(teamId);
    return ApiMethods.getUrl(url);
  };

  static getLanguages = (defaultLanguage) => {
    const url = ENDPOINTS.GET_LANGUAGES(defaultLanguage);
    return ApiMethods.getUrl(url);
  };
  static putApkValidation = (applicationId, submissionId) => {
    const url = ENDPOINTS.PUT_APK_VALIDATION(applicationId, submissionId);
    return ApiMethods.put(url);
  };
  static putSubmission = (applicationId, submissionId, data) => {
    const url = ENDPOINTS.PUT_SUBMISSION(applicationId, submissionId);
    return ApiMethods.put(url, data);
  };

  static putDomainApproverSubmission = (applicationId, submissionId, data) => {
    const url = ENDPOINTS.PUT_SUBMISSION(applicationId, submissionId);
    return ApiMethods.put(url, data);
  };

  static putGlobalAdminGarageModeSubmission = (
    applicationId,
    submissionId,
    data
  ) => {
    const url = ENDPOINTS.PUT_GLOBAL_ADMIN_GARAGE_MODE_SUBMISSION(
      applicationId,
      submissionId
    );
    return ApiMethods.put(url, data);
  };

  static getAllTeams = () => {
    const url = ENDPOINTS.GET_ALL_TEAMS();
    return ApiMethods.getUrl(url);
  };

  static putAddTeamMember = (teamId, teamData) => {
    const url = ENDPOINTS.PUT_ADD_TEAM_MEMBER(teamId);
    return ApiMethods.put(url, teamData);
  };

  static patchAddTeamMember = (teamId, teamData) => {
    const url = ENDPOINTS.PATCH_ADD_TEAM_MEMBER(teamId);
    return ApiMethods.patch(url, teamData);
  };

  static deleteTeamMember = (teamId, teamData) => {
    const url = ENDPOINTS.DELETE_ADD_TEAM_MEMBER(teamId);
    return ApiMethods.deleteData(url, teamData);
  };

  static getCountries = () => {
    const url = ENDPOINTS.GET_COUNTRIES();
    return ApiMethods.getUrl(url);
  };

  static getVehicles = () => {
    const url = ENDPOINTS.GET_VEHICLES();
    return ApiMethods.getUrl(url);
  };

  static getDomainApproverVehicles = (domainAliasId) => {
    const url = ENDPOINTS.GET_DOMAIN_APPROVER_VEHICLES(domainAliasId);
    return ApiMethods.getUrl(url);
  };

  static getCpus = () => {
    const url = ENDPOINTS.GET_CPUS();
    return ApiMethods.getUrl(url);
  };

  static getPortalConfigSettings = () => {
    const url = ENDPOINTS.GET_SETTING_RESPONSE();
    return ApiMethods.getAdminUrl(url);
  };

  static getPortalConfigGlobalAdmin = () => {
    const url = ENDPOINTS.GET_PORTAL_CONFIG_GLOBAL_ADMIN();
    return ApiMethods.getUrl(url);
  };

  static getOEMAppOverviewData = (contentId) => {
    const url = ENDPOINTS.GET_OEM_APP_OVERVIEW_DATA(contentId);
    return ApiMethods.getAdminUrl(url);
  };

  static getGlobalAdminAppOverviewData = (contentId) => {
    const url = ENDPOINTS.GET_GLOBAL_APP_OVERVIEW_DATA(contentId);
    return ApiMethods.getUrl(url);
  };
  static getApplicationSummaryGlobalAdmin = () => {
    const url = ENDPOINTS.GET_APPLICATION_SUMMARY_GLOBAL_ADMIN();
    return ApiMethods.getUrl(url);
  };
  static getGlobalAdminAnalyticsData = (contentId) => {
    const url = ENDPOINTS.GET_GLOBAL_ADMIN_ANALYTICS_DATA(contentId);
    const data = { isAdmin: true };
    return ApiMethods.post(url, data);
  };

  static getOEMAppBasedOnVersionCode = (contentId, versionCode) => {
    const url = ENDPOINTS.GET_OEM_APP_BASED_ON_VERSION_CODE(
      contentId,
      versionCode
    );
    return ApiMethods.getAdminUrl(url);
  };

  static getGlobalAppBasedOnVersionCode = (contentId, versionCode) => {
    const url = ENDPOINTS.GET_GLOBAL_APP_BASED_ON_VERSION_CODE(
      contentId,
      versionCode
    );
    return ApiMethods.getUrl(url);
  };

  static uploadScreenShotDetails = (
    applicationId,
    submissionId,
    defaultLanguage,
    image
  ) => {
    const url = ENDPOINTS.POST_SCREENSHOT(
      applicationId,
      submissionId,
      defaultLanguage
    );
    return ApiMethods.apiRequestFormData("POST", url, image);
  };

  static postSubmitReassignOwnership = (applicationId, payloadId) => {
    const url = ENDPOINTS.POST_SUBMIT_REASSIGN_OWNERSHIP(
      applicationId,
      payloadId
    );
    return ApiMethods.post(url);
  };

  static postExportToExcel = () => {
    const url = ENDPOINTS.POST_EXPORT_TO_EXCEL();
    return ApiMethods.post(url);
  };

  static postFilterReassignOwnership = (
    pageNumber,
    pageSize,
    sortBy,
    sortOrder,
    ignoreCase,
    searchType,
    payload
  ) => {
    const url = ENDPOINTS.POST_FILTER_REASSIGN_OWNERSHIP(
      pageNumber,
      pageSize,
      sortBy,
      sortOrder,
      ignoreCase,
      searchType
    );
    return ApiMethods.post(url, payload);
  };

  static deleteScreenShots = (
    applicationId,
    submissionId,
    screenShotId,
    defaultLanguage
  ) => {
    const url = ENDPOINTS.DELETE_SCREENSHOT(
      applicationId,
      submissionId,
      screenShotId,
      defaultLanguage
    );
    return ApiMethods.delete(url);
  };

  static uploadBannerDetails = (
    applicationId,
    submissionId,
    defaultLanguage,
    image
  ) => {
    const url = ENDPOINTS.UPLOAD_BANNER_FILE(
      applicationId,
      submissionId,
      defaultLanguage
    );
    return ApiMethods.apiRequestFormData("POST", url, image);
  };

  static deleteBannerFile = (
    applicationId,
    submissionId,
    bannerId,
    defaultLanguage
  ) => {
    const url = ENDPOINTS.DELETE_BANNER_FILE(
      applicationId,
      submissionId,
      bannerId,
      defaultLanguage
    );
    return ApiMethods.delete(url);
  };

  static uploadIconFile = (applicationId, submissionId, image) => {
    const url = ENDPOINTS.UPLOAD_NEW_ICON_FILE(applicationId, submissionId);
    return ApiMethods.apiRequestFormData("POST", url, image);
  };

  static uploadAdditionalDocument = (applicationId, submissionId, file) => {
    const url = ENDPOINTS.UPLOAD_ATTACHMENT_FILE(applicationId, submissionId);
    return ApiMethods.apiRequestFormData("POST", url, file);
  };

  static deleteAdditionalDocument = (applicationId, submissionId, fileName) => {
    const url = ENDPOINTS.DELETE_ATTACHMENT_FILE(
      applicationId,
      submissionId,
      fileName
    );
    return ApiMethods.delete(url);
  };

  static postCreateApp = (
    applicationName,
    selectedLang,
    selectedTeam,
    formData
  ) => {
    const url = ENDPOINTS.POST_CREATE_APP(
      applicationName,
      selectedLang,
      selectedTeam
    );

    return ApiMethods.postAPKFileUpload(url, formData);
  };

  static getAllApplication = (status) => {
    const url = ENDPOINTS.GET_ALL_APPLICATIONS(status);
    return ApiMethods.getUrl(url);
  };

  static getDeletedApplications = () => {
    const url = ENDPOINTS.GET_DELETED_APPLICATIONS();
    return ApiMethods.getUrl(url);
  };

  static getAllApplicationBasedOnStatus = (status) => {
    if (status === "OEM_ACTIVITY_STATUS") return;
    const url = ENDPOINTS.GET_ALL_APPLICATIONS_STATUS(status);
    return ApiMethods.getUrl(url);
  };

  static getAppOemStatus = (packageId, appVersion) => {
    const url = ENDPOINTS.GET_APP_OEM_STATUS(packageId, appVersion);
    return ApiMethods.getUrl(url, { useCache: false });
  };

  static getAllFeatures = () => {
    const url = ENDPOINTS.GET_ALL_FEATURES();
    return ApiMethods.getUrl(url);
  };

  static getAllFeaturesBasedOnStatus = (status) => {
    const url = ENDPOINTS.GET_ALL_FEATURES_STATUS(status);
    return ApiMethods.getUrl(url);
  };

  static postCreateFeature = (
    featureName,
    selectedLang,
    selectedTeam,
    featureUniqueId,
    formData
  ) => {
    const url = ENDPOINTS.POST_CREATE_FEATURE(
      featureName,
      selectedLang,
      selectedTeam,
      featureUniqueId
    );
    return ApiMethods.post(url, formData);
  };

  static getCognitoSelf = () => {
    const url = ENDPOINTS.GET_COGNITO_SELF();
    return ApiMethods.getUrl(url);
  };

  static getAssociateSoftwareToken = () => {
    const url = ENDPOINTS.GET_ASSOCIATE_SOFTWARE_TOKEN();
    return ApiMethods.getUrl(url);
  };

  static patchVerifySoftwareToken = (data) => {
    const url = ENDPOINTS.PATCH_VERIFY_SOFTWARE_TOKEN(data);
    return ApiMethods.patch(url, data);
  };

  static patchCognitoSelf = (data) => {
    const url = ENDPOINTS.PATCH_COGNITO_SELF(data);
    return ApiMethods.patch(url, data);
  };
  static patchCognitoAdminSelf = (data) => {
    const url = ENDPOINTS.PATCH_COGNITO_ADMIN_SELF(data);
    return ApiMethods.patch(url, data);
  };

  static getCognitoAdminSelf = () => {
    const url = ENDPOINTS.GET_COGNITO_ADMIN_SELF();
    return ApiMethods.getUrl(url);
  };
  static sendSubmissionForProductionDelistAndRelist = (
    appId,
    submissionId,
    status,
    forceUninstall
  ) => {
    const url = ENDPOINTS.SEND_SUBMISSION_FOR_PRODUCTION_DELIST_AND_RELIST(
      appId,
      submissionId,
      status,
      forceUninstall
    );
    return ApiMethods.post(url);
  };

  static sendSubmissionForProductionRelistAndReview = (
    appId,
    submissionId,
    status
  ) => {
    const url = ENDPOINTS.SUBMIT_FOR_PRODUCTION_RELIST_AND_REVIEW(
      appId,
      submissionId,
      status
    );
    return ApiMethods.post(url);
  };

  static callSubmissionsForCreateNewRelease = (appId) => {
    const url = ENDPOINTS.CALLING_APPLICATION_SUBMISSIONS(appId);
    return ApiMethods.post(url);
  };

  static sendSubmissionForProductionDomainApprover = (
    appId,
    submissionId,
    status,
    comment,
    file,
    reportLink = ""
  ) => {
    const url = ENDPOINTS.SUBMIT_FOR_PRODUCTION(
      appId,
      submissionId,
      status,
      comment
    );
    return ApiMethods.apiRequestFormData("POST", url, file, null, reportLink);
  };

  static postNotifyDeveloper = (payload) => {
    const url = ENDPOINTS.POST_NOTIFY_DEVELOPER();
    return ApiMethods.post(url, payload);
  };

  static postSubmissionDetailsDomainApproverForProduction = (
    applicationId,
    submissionId,
    status,
    file,
    comment = "",
    reportLink = ""
  ) => {
    const url = ENDPOINTS.SUBMIT_FOR_PRODUCTION_DOMAIN_APPROVER(
      applicationId,
      submissionId,
      status,
      comment
    );
    return ApiMethods.apiRequestFormData("POST", url, file, null, reportLink);
  };

  static uploadBinaryFile = (appId, submissionId, file, onProgress) => {
    const url = ENDPOINTS.UPLOAD_BINARY_FILE(appId, submissionId);
    return ApiMethods.apiRequestFormData("POST", url, file, onProgress, "");
  };
  static uploadSignedBinaryFile = (appId, submissionId, file) => {
    const url = ENDPOINTS.UPLOAD_SIGNED_BINARY_FILE(appId, submissionId);
    return ApiMethods.apiRequestFormData("POST", url, file);
  };

  static deleteApplication = (applicationId, submissionId) => {
    const url = ENDPOINTS.DELETE_APPLICATION(applicationId, submissionId);
    return ApiMethods.delete(url);
  };

  static deleteFeature(featureId) {
    const url = ENDPOINTS.DELETE_FEATURE(featureId);
    return ApiMethods.delete(url);
  }

  static getAllSubmissions = (applicationId) => {
    const url = ENDPOINTS.GET_ALL_SUBMISSIONS(applicationId);
    return ApiMethods.getUrl(url);
  };

  static getIds = () => {
    const url = ENDPOINTS.GET_ALL_IDS();
    return ApiMethods.getAdminUrl(url);
  };

  static postSellerDashboardTotalData = (
    packageName,
    data,
    startDate,
    endDate
  ) => {
    const url = ENDPOINTS.POST_SELLER_DASHBOARD_TOTAL_DATA(
      packageName,
      startDate,
      endDate
    );
    return ApiMethods.post(url, data);
  };

  static getSellerDashboardOverviewData = (packageName) => {
    const url = ENDPOINTS.GET_SELLER_DASHBOARD_OVERVIEW_DATA(packageName);
    return ApiMethods.getUrl(url);
  };

  static postSellerDashboardInstallsData = (
    packageName,
    data,
    startDate,
    endDate
  ) => {
    const url = ENDPOINTS.POST_SELLER_DASHBOARD_INSTALLS_DATA(
      packageName,
      startDate,
      endDate
    );
    return ApiMethods.post(url, data);
  };

  static postSellerCrashOverviewData = (
    packageName,
    data,
    startDate,
    endDate
  ) => {
    const url = ENDPOINTS.POST_SELLER_CRASH_OVERVIEW_DATA(
      packageName,
      startDate,
      endDate
    );
    return ApiMethods.post(url, data);
  };

  static postSellerCrashStackTraceData = (
    packageName,
    data,
    startDate,
    endDate
  ) => {
    const url = ENDPOINTS.POST_SELLER_CRASH_STACKTRACE_DATA(
      packageName,
      startDate,
      endDate
    );
    return ApiMethods.post(url, data);
  };

  static getClusters = () => {
    const url = ENDPOINTS.GET_CLUSTERS();
    return ApiMethods.getUrl(url);
  };

  static getLegalDocuments = () => {
    const url = ENDPOINTS.GET_LEGAL_DOCS();
    return ApiMethods.getUrl(url);
  };

  static deleteBinaryDetaila = (appId, submissionId, binaryKey) => {
    const url = ENDPOINTS.DELETE_BINARY_KEY(appId, submissionId, binaryKey);
    return ApiMethods.delete(url);
  };

  static deleteDescriptions = (appId, submissionId, languageArray) => {
    const url = ENDPOINTS.DELETE_DESCRIPTIONS(appId, submissionId);
    return ApiMethods.delete(url, languageArray);
  };

  static getUserData = (userId, teamId) => {
    const url = ENDPOINTS.GET_USER_DATA(userId, teamId);
    return ApiMethods.getUrl(url);
  };

  static getGlobalAdminUsers = (statusData) => {
    const url = ENDPOINTS.GET_GLOBAL_ADMIN_USERS();
    return ApiMethods.post(url, statusData);
  };

  static getGlobalAdminUserById = (userId) => {
    const url = ENDPOINTS.GET_GLOBAL_ADMIN_USER_BY_ID(userId);
    return ApiMethods.getUrl(url);
  };

  static getGlobalAdminOverView = (
    isAppListNeeded,
    isCountNeeded,
    isAppCountNeeded,
    offset,
    limit,
    searchText,
    requestBody
  ) => {
    const url = ENDPOINTS.GET_GLOBAL_ADMIN_OVER_VIEW(
      isAppListNeeded,
      isCountNeeded,
      isAppCountNeeded,
      offset,
      limit
    );

    return ApiMethods.post(url, requestBody);
  };

  static getGlobalAdminDetails = (requestBody) => {
    const url = ENDPOINTS.GET_GLOBAL_ADMIN_DETAILS();

    return ApiMethods.post(url, requestBody);
  };

  static getGlobalAdminDownloadDetails = (requestBody) => {
    const url = ENDPOINTS.GET_GLOBAL_ADMIN_DOWNLOAD_DETAILS();

    return ApiMethods.post(url, requestBody);
  };

  static getGlobalAdminFlexMetadata = () => {
    const url = ENDPOINTS.GET_GLOBAL_ADMIN_FLEX_METADATA();
    return ApiMethods.getUrl(url);
  };

  static createGlobalAdminFlexMetadata = (flexDataPayload) => {
    const url = ENDPOINTS.CREATE_GLOBAL_ADMIN_FLEX_METADATA();
    return ApiMethods.post(url, flexDataPayload);
  };

  static updateGlobalAdminFlexMetadata = (flexDataPayload, id) => {
    const url = ENDPOINTS.UPDATE_GLOBAL_ADMIN_FLEX_METADATA();
    return ApiMethods.post(url, { id, ...flexDataPayload });
  };

  static deleteGlobalAdminFlexMetadata = (id) => {
    const url = ENDPOINTS.DELETE_GLOBAL_ADMIN_FLEX_METADATA(id);
    return ApiMethods.delete(url);
  };

  static getGlobalAdminFlexSubmissionAPIData = (appId, submissionId) => {
    const url = ENDPOINTS.GET_GLOBAL_ADMIN_FLEX_SUBMISSION_API_DATA(
      appId,
      submissionId
    );
    return ApiMethods.getUrl(url);
  };

  static setGlobalAdminFlexSubmissionAPIData = (
    appId,
    submissionId,
    payload
  ) => {
    const url = ENDPOINTS.SET_GLOBAL_ADMIN_FLEX_SUBMISSION_API_DATA(
      appId,
      submissionId
    );
    return ApiMethods.put(url, payload);
  };

  static getAcceptUserDomains = () => {
    const url = ENDPOINTS.GET_ACCEPT_USER_DOMAINS();
    return ApiMethods.getUrl(url);
  };

  static getAcceptUserCompanies = () => {
    const url = ENDPOINTS.GET_ACCEPT_USER_COMPANIES();
    return ApiMethods.getUrl(url);
  };

  static addNewCompanyMethod = (data) => {
    const url = ENDPOINTS.ADD_NEW_COMPANY();
    return ApiMethods.post(url, data);
  };

  static acceptUserRequest = (data, id) => {
    const url = ENDPOINTS.ACCEPT_USER_REQUEST(id);
    return ApiMethods.post(url, data);
  };

  static rejectUserRequest = (data, id) => {
    const url = ENDPOINTS.REJECT_USER_REQUEST(id);
    return ApiMethods.post(url, data);
  };

  static deactivateUser = (id, deactivatePayload) => {
    const url = ENDPOINTS.DEACTIVATE_USER(id);
    return ApiMethods.post(url, deactivatePayload);
  };

  static reactivateUser = (id) => {
    const url = ENDPOINTS.REACTIVATE_USER(id);
    return ApiMethods.put(url);
  };

  static deleteGlobalAdminUser = (id, checkPurgeData, forceUninstall) => {
    const url = ENDPOINTS.DELETE_GLOBAL_ADMIN_USER(
      id,
      checkPurgeData,
      forceUninstall
    );
    return ApiMethods.deleteUser(url);
  };

  static getOemAdminRoles = () => {
    const url = ENDPOINTS.GET_ACCEPT_USER_OEM_ADMIN_ROLES();
    return ApiMethods.getUrl(url);
  };

  static getOverViewData = (
    isAppListNeeded,
    isCountNeeded,
    isAppCountNeeded,
    offset,
    limit,
    searchText,
    requestBody
  ) => {
    const url = ENDPOINTS.GET_OVER_VIEW_DATA(
      isAppListNeeded,
      isCountNeeded,
      isAppCountNeeded,
      offset,
      limit
    );

    return ApiMethods.postAdminUrl(url, requestBody);
  };

  static getUserInfo = () => {
    const url = ENDPOINTS.GET_USER_INFO();
    return ApiMethods.getUrl(url);
  };

  static getAllInApp = (sellerId, packageId, skipInvalidProducts) => {
    const url = ENDPOINTS.GET_ALL_IN_APP(
      sellerId,
      packageId,
      skipInvalidProducts
    );
    return ApiMethods.getUrl(url);
  };
  static getExchangeRates = (basePrice, currencyCode) => {
    const url = ENDPOINTS.GET_EXCHANGE_RATES(basePrice, currencyCode);
    return ApiMethods.getUrl(url);
  };
  static saveInApp = (data) => {
    const url = ENDPOINTS.POST_ADD_PRODUCT();
    return ApiMethods.post(url, data);
  };
  static updateInApp = (data, productId) => {
    const url = ENDPOINTS.PUT_UPDATE_PRODUCT_ONE_TIME(productId);
    return ApiMethods.putWithoutResponse(url, data);
  };
  static saveInAppWithEnableRecerence = (data) => {
    const url = ENDPOINTS.POST_ADD_PRODUCT_ENABLE_RECURRENCE();
    return ApiMethods.post(url, data);
  };
  static updateInAppRecurrence = (data, productId) => {
    const url = ENDPOINTS.PUT_UPDATE_PRODUCT_ENABLE_RECURRENCE(productId);
    return ApiMethods.putWithoutResponse(url, data);
  };
  static getOneTimeAppDetails = (productId) => {
    const url = ENDPOINTS.GET_ONE_TIME_PRODUCT(productId);
    return ApiMethods.getUrl(url);
  };
  static getRecurringAppDetails = (productId) => {
    const url = ENDPOINTS.GET_RECURRING_PRODUCT(productId);
    return ApiMethods.getUrl(url);
  };
  static savePlanInApp = (data) => {
    const url = ENDPOINTS.POST_ADD_PLAN_IN_APP();
    return ApiMethods.post(url, data);
  };
  static getPlanDetails = (productId, planId) => {
    const url = ENDPOINTS.GET_PLAN_DETAILS(productId, planId);
    return ApiMethods.getUrl(url);
  };
  static getAllPremiumApps = (sellerId, packageId, skipInvalidProducts) => {
    const url = ENDPOINTS.GET_ALL_PREMIUM_APP(
      sellerId,
      packageId,
      skipInvalidProducts
    );
    return ApiMethods.getUrl(url);
  };
  static getContentData = (status, isCountNeeded) => {
    const url = ENDPOINTS.GET_CONTENTS_DATA(status, isCountNeeded);
    return ApiMethods.getAdminUrl(url);
  };
  static getAdminCountries = () => {
    const url = ENDPOINTS.GET_ADMIN_COUNTRIES();
    return ApiMethods.getAdminUrl(url);
  };

  static getGlobalAdminCountries = () => {
    const url = ENDPOINTS.GET_GLOBAL_ADMIN_COUNTRIES();
    return ApiMethods.getUrl(url);
  };

  static getAdminCategories = () => {
    const url = ENDPOINTS.GET_ADMIN_CATEGORIES();
    return ApiMethods.getUrl(url);
  };

  static getAdminOEM = () => {
    const url = ENDPOINTS.GET_ADMIN_OEM();
    return ApiMethods.getUrl(url);
  };

  static getmmyYears = () => {
    const url = ENDPOINTS.GET_MMY_YEARS();
    return ApiMethods.getUrl(url);
  };

  static saveMakeModelYear = (manufacturerId, data, modelId = null) => {
    if (manufacturerId === null) {
      // Creating a new make - use POST
      const url = ENDPOINTS.POST_ADD_MMY();
      return ApiMethods.post(url, data);
    } else {
      // Updating existing make - use PUT
      const url = ENDPOINTS.PUT_ADD_MMY(manufacturerId, modelId);
      return ApiMethods.put(url, data);
    }
  };

  static getAdminDomain = () => {
    const url = ENDPOINTS.GET_ADMIN_DOMAIN();
    return ApiMethods.getUrl(url);
  };

  static getAdminCPUTypes = () => {
    const url = ENDPOINTS.GET_ADMIN_CPU_TYPES();
    return ApiMethods.getUrl(url);
  };

  static getGlobalAdminEcuTypes = () => {
    const url = ENDPOINTS.GET_GLOBAL_ADMIN_ECUTYPES();
    return ApiMethods.getUrl(url);
  };

  static getPortfolioContentDetails = (portfolioId, contentId) => {
    const url = ENDPOINTS.GET_PORTFOLIO_CONTENTS_DETAILS(
      portfolioId,
      contentId
    );
    return ApiMethods.getAdminUrl(url);
  };
  static postAdminTableauUrl = () => {
    const url = ENDPOINTS.POST_TABLEAU_URL();
    return ApiMethods.postOemAdminTableauUrl(url);
  };

  static postAdminTableauUrlGobalAdmin = () => {
    const url = ENDPOINTS.POST_TABLEAU_URL_GLOBAL_ADMIN();
    return ApiMethods.postGlobalAdminTableau(url);
  };

  static getOEMAdminEcuTypes = () => {
    const url = ENDPOINTS.GET_OEMADMIN_ECUTYPES();
    return ApiMethods.getAdminUrl(url);
  };

  static getEcuTypes = () => {
    const url = ENDPOINTS.GET_ADMIN_ECU_TYPES();
    return ApiMethods.getAdminUrl(url);
  };

  // Domain Approver specific methods
  static getDomainApproverEcuTypes = () => {
    const url = ENDPOINTS.GET_DOMAIN_APPROVER_ECU_TYPES();
    return ApiMethods.getUrl(url);
  };

  static getDomainApproverCountries = () => {
    const url = ENDPOINTS.GET_DOMAIN_APPROVER_COUNTRIES();
    return ApiMethods.getUrl(url);
  };
  static getOemAdminMmy = (appId) => {
    const url = ENDPOINTS.GET_OEM_ADMIN_MMY(appId);
    return ApiMethods.getAdminUrl(url);
  };
  static savePlanPremium = (data) => {
    const url = ENDPOINTS.POST_ADD_PLAN_PREMIUM();
    return ApiMethods.post(url, data);
  };
  static updatePlanPremium = (planId, data) => {
    const url = ENDPOINTS.PUT_UPDATE_PLAN_PREMIUM(planId);
    return ApiMethods.putWithoutResponse(url, data);
  };
  static deleteAppPricing = (packageId) => {
    const url = ENDPOINTS.DELETE_APP_PRICING(packageId);
    return ApiMethods.delete(url, {});
  };
  static getOEMCategories = (portfolio) => {
    const url = ENDPOINTS.GET_OEM_ADMIN_CATEGORIES(portfolio);
    return ApiMethods.getAdminUrl(url);
  };
  static saveCategorySequence = (data, portfolio) => {
    const url = ENDPOINTS.PATCH_OEM_ADMIN_CATEGORIES(portfolio);
    return ApiMethods.patchAdminUrl(url, data);
  };
  static getOemSettingsEmailNotification = () => {
    const url = ENDPOINTS.GET_OEM_SETTINGS_EMAIL_NOTIFICATION();
    return ApiMethods.getAdminUrl(url);
  };

  static getDomainApproverHeadUnit = (domainAliasId) => {
    const url = ENDPOINTS.GET_DOMAIN_APPROVER_HEAD_UNIT(domainAliasId);
    return ApiMethods.getUrl(url);
  };

  static putNewEmailAddressForOemSettings = (emailRecipients) => {
    const url = ENDPOINTS.PUT_NEW_EMAIL_ADDRESS_FOR_OEM_SETTINGS();
    return ApiMethods.putAdminUrl(url, emailRecipients);
  };
  static getAgeRatings = () => {
    const url = ENDPOINTS.GET_AGE_RATING();
    return ApiMethods.getUrl(url);
  };
  static getRecommendedApps = (portfolio) => {
    const url = ENDPOINTS.GET_OEM_ADMIN_RECOMMENDED_APPS(portfolio);
    return ApiMethods.getAdminUrl(url);
  };
  static getSuggestedApps = (portfolio) => {
    const url = ENDPOINTS.GET_OEM_ADMIN_SUGGESTED_APPS(portfolio);
    return ApiMethods.getAdminUrl(url);
  };
  static getAllCountries = () => {
    const url = ENDPOINTS.GET_OEM_ADMIN_ALL_COUNTRIES();
    return ApiMethods.getAdminUrl(url);
  };
  static searchOEMAdminApps = (
    portfolio,
    locale,
    searchText,
    countries,
    ecutypes
  ) => {
    const url = ENDPOINTS.GET_OEM_ADMIN_SEARCH_APPS(
      portfolio,
      locale,
      searchText,
      countries,
      ecutypes
    );
    return ApiMethods.getAdminUrl(url);
  };
  static searchOEMAdminSuggestedApps = (
    portfolio,
    locale,
    searchText,
    countries,
    ecutypes
  ) => {
    const url = ENDPOINTS.GET_OEM_ADMIN_SUGGESTED_SEARCH_APPS(
      portfolio,
      locale,
      searchText,
      countries,
      ecutypes
    );
    return ApiMethods.getAdminUrl(url);
  };
  static patchAddRecommendedApp = (portfolio, payload, packageId) => {
    const url = ENDPOINTS.PATCH_OEM_ADMIN_ADD_RECOMMENDED_APP(
      portfolio,
      packageId
    );
    return ApiMethods.patchAdminUrl(url, payload);
  };
  static deleteRecommendedApp = (portfolio, packageId) => {
    const url = ENDPOINTS.DELETE_OEM_ADMIN_RECOMMENDED_APP(
      portfolio,
      packageId
    );
    return ApiMethods.deleteAdminUrl(url);
  };
  static patchAddSuggestedApp = (portfolio, payload, packageId) => {
    const url = ENDPOINTS.PATCH_OEM_ADMIN_ADD_SUGGESTED_APP(
      portfolio,
      packageId
    );
    return ApiMethods.patchAdminUrl(url, payload);
  };
  static deleteSuggestedApp = (portfolio, packageId) => {
    const url = ENDPOINTS.DELETE_OEM_ADMIN_SUGGESTED_APP(portfolio, packageId);
    return ApiMethods.deleteAdminUrl(url);
  };
  static updatePortfolioContentDetails = (
    portfolioId,
    contentId,
    tagsData,
    reasonForExclusion
  ) => {
    const url = ENDPOINTS.PATCH_PORTFOLIO_CONTENTS_DETAILS(
      portfolioId,
      contentId
    );

    // Clean the tags data to remove any properties that might cause issues
    const cleanedTagsData = tagsData.map((tag) => {
      // Only include valid properties recognized by the backend
      const { key, values, rule, operator } = tag;
      return { key, values, rule, operator };
    });

    const formData = new FormData();
    formData.append("tags", JSON.stringify(cleanedTagsData));

    // Add reasonForExclusion as a separate parameter if provided
    if (reasonForExclusion) {
      // Check if reasonForExclusion is an object with reasonForCountryExclusion property
      if (reasonForExclusion.reasonForCountryExclusion) {
        formData.append(
          "reasonForCountryExclusion",
          reasonForExclusion.reasonForCountryExclusion
        );
      } else {
        // If it's a simple string value
        formData.append("reasonForExclusion", reasonForExclusion);
      }
    }

    return ApiMethods.patchAdminUrl(url, formData);
  };

  static updatePortfolioContentDetailsForPreInstall = (
    portfolioId,
    contentId,
    formData
  ) => {
    const url = ENDPOINTS.PATCH_PORTFOLIO_CONTENTS_DETAILS(
      portfolioId,
      contentId
    );

    // Clean the tags data to remove any properties that might cause issues
    //   // Only include valid properties recognized by the backend


    return ApiMethods.patchAdminUrl(url, formData);
  };

  static approvAndRejectApps = (portfolioId, contentId, approveRejectData) => {
    const url = ENDPOINTS.PATCH_OEM_ADMIN_APPROVED_REJECT_APP(
      portfolioId,
      contentId
    );

    return ApiMethods.patchAdminUrl(url, approveRejectData);
  };
  static approvAndRejectDomainApproverThirdPartyApps = (
    contentId,
    approveRejectData
  ) => {
    const url = ENDPOINTS.PATCH_DOMAIN_APPROVER_APPROVED_REJECT_APPS(contentId);
    const formData = new FormData();
    formData.append(
      "domainApprovalStatus",
      approveRejectData.domainApprovalStatus
    );
    formData.append(
      "domainApproverComments",
      approveRejectData.domainApproverComments
    );
    if (approveRejectData.domainApprovalStatus === "DOMAIN_APPROVAL_APPROVED") {
      if (approveRejectData.file) {
        formData.append("file", approveRejectData.file);
      }
      if (approveRejectData.link) {
        formData.append("link", approveRejectData.link);
      }
    }
    return ApiMethods.patchAdminUrl(url, formData);
  };

  static getTargetEcuTypes = (portfolioId, contentId) => {
    const url = ENDPOINTS.GET_OEM_ADMIN_TARGET_ECUTYPES(portfolioId, contentId);
    return ApiMethods.getAdminUrl(url);
  };

  static getApprovalHistoryData = (portfolioId, contentId) => {
    const url = ENDPOINTS.GET_OEM_ADMIN_APPROVAL_HISTORY(
      portfolioId,
      contentId
    );
    return ApiMethods.getAdminUrl(url);
  };

  static postGenerateClient = (appId) => {
    const url = ENDPOINTS.GENERATE_CLIENT(appId);
    return ApiMethods.post(url);
  };
  static getGenerateClient = (appId) => {
    const url = ENDPOINTS.GENERATE_CLIENT(appId);
    return ApiMethods.getUrl(url);
  };

  static deleteGenerateClient = (clientId) => {
    const url = ENDPOINTS.GENERATE_CLIENT(clientId);
    return ApiMethods.delete(url, {});
  };
  static getAppPricing = (sellerId, packageName, type) => {
    const url = ENDPOINTS.GET_APP_PRICING(sellerId, packageName, type);
    return ApiMethods.getAdminUrl(url);
  };

  static getProductDetails = (id, enableRecurrence) => {
    const url = ENDPOINTS.GET_PRODUCT_DETAILS(id, enableRecurrence);
    return ApiMethods.getAdminUrl(url);
  };

  static getOEMAdminPlanDetails = (productId, PlanId) => {
    const url = ENDPOINTS.GET_OEM_ADMIN_PLAN_DETAILS(productId, PlanId);
    return ApiMethods.getAdminUrl(url);
  };
  static getOemAdminLocales = () => {
    const url = ENDPOINTS.GET_OEM_ADMIN_LOCALES();
    return ApiMethods.getAdminUrl(url);
  };
  static getOemAdminLegalDocumentInfos = () => {
    const url = ENDPOINTS.GET_OEM_LEGAL_DOCUMENT_INFOS();
    return ApiMethods.getAdminUrl(url);
  };
  static patchOemAdminLegalDocumentInfos = (payload, language) => {
    const url = ENDPOINTS.PATCH_OEM_LEGAL_DOCUMENT_INFOS(language);
    return ApiMethods.patchAdminUrl(url, payload);
  };
  static getOemAdminLegalInfos = () => {
    const url = ENDPOINTS.GET_OEM_LEGAL_INFOS();
    return ApiMethods.getAdminUrl(url);
  };
  static getDomainApproverContent = () => {
    const url = ENDPOINTS.GET_DOMAIN_APPROVER_CONTENTS();
    return ApiMethods.getAdminUrl(url);
  };
  static postDomainApproverClusters = (clusterData) => {
    const url = ENDPOINTS.POST_DOMAIN_APPROVER_CLUSTERS();
    return ApiMethods.post(url, clusterData);
  };

  static deleteDeletedApplication = (applicationId) => {
    const url = ENDPOINTS.DELETE_DELETED_APPLICATION(applicationId);
    return ApiMethods.delete(url);
  };

  static getAllPublisherIds = (data) => {
    const url = ENDPOINTS.GET_ALL_PUBLISHER_IDS();
    return ApiMethods.post(url, data);
  };

  static rejectDeletedApplication = (applicationId) => {
    const url = ENDPOINTS.REJECT_DELETED_APPLICATION(applicationId);
    return ApiMethods.post(url, null);
  };

  static getVehicleLists = (portfolioId) => {
    const url = ENDPOINTS.GET_OEM_ADMIN_VEHICLE_LISTS(portfolioId);
    return ApiMethods.getAdminUrl(url);
  };

  static createVehicleList = (portfolioId, vehicleListData) => {
    const url = ENDPOINTS.CREATE_OEM_ADMIN_VEHICLE_LIST(portfolioId);
    return ApiMethods.postAdminUrl(url, vehicleListData);
  };

  static uploadVehicleListFile = (portfolioId, vehicleListId, file) => {
    const url = ENDPOINTS.UPLOAD_OEM_ADMIN_VEHICLE_LIST(
      portfolioId,
      vehicleListId
    );
    const formData = new FormData();
    formData.append("file", file);
    return ApiMethods.postAdminUrl(url, formData);
  };

  static deleteVehicleList = (portfolioId, groupId) => {
    const url = ENDPOINTS.DELETE_OEM_ADMIN_VEHICLE_LIST(portfolioId, groupId);
    return ApiMethods.deleteAdminUrl(url);
  };
  static getVehicleListForGroup = (portfolioId, groupId) => {
    const url = ENDPOINTS.GET_OEM_ADMIN_VEHICLES_FOR_GROUP(
      portfolioId,
      groupId
    );
    return ApiMethods.getAdminUrl(url);
  };
  static addVehicleToGroup = (portfolioId, groupId, vehicleData) => {
    const url = ENDPOINTS.POST_OEM_ADMIN_VEHICLES_FOR_GROUP(
      portfolioId,
      groupId
    );
    return ApiMethods.postAdminUrl(url, vehicleData);
  };
  static deleteVehicleFromGroup = (portfolioId, groupId, memberId) => {
    const url = ENDPOINTS.DELETE_OEM_ADMIN_VEHICLES_FROM_GROUP(
      portfolioId,
      groupId,
      memberId
    );
    return ApiMethods.deleteAdminUrl(url);
  };
  static associateGroupWithApplication = (portfolioId, packageId, data) => {
    const url = ENDPOINTS.POST_OEM_ADMIN_GROUP_TO_APPLICATION(
      portfolioId,
      packageId
    );
    return ApiMethods.postAdminUrl(url, data);
  };
  static getGroupsForApplication = (portfolioId, packageId) => {
    const url = ENDPOINTS.GET_OEM_ADMIN_GROUP_FOR_APPLICATION(
      portfolioId,
      packageId
    );
    return ApiMethods.getAdminUrl(url);
  };
  static deleteGroupFromApplication = (portfolioId, groupId, packageId) => {
    const url = ENDPOINTS.DELETE_OEM_ADMIN_GROUP_FROM_APPLICATION(
      portfolioId,
      groupId,
      packageId
    );
    return ApiMethods.deleteAdminUrl(url);
  };

  static patchGroupForApplication = (portfolioId, groupId, data) => {
    const url = ENDPOINTS.PATCH_OEM_ADMIN_GROUP_TO_APPLICATION(portfolioId, groupId);
    return ApiMethods.patchAdminUrl(url, data);
  }
  static putDSAUrl = (portfolioId, data) => {
    const url = ENDPOINTS.PUT_DSA_URL(portfolioId);
    return ApiMethods.DeletePortfolioSettingURL(url, data);
  };

  static getDSAUrl = (portfolioId) => {
    const url = ENDPOINTS.GET_DSA_URL(portfolioId);
    return ApiMethods.getAdminUrl(url);
  };

  static patchDSAUrl = (portfolioId, data) => {
    const url = ENDPOINTS.PATCH_DSA_URL(portfolioId);
    return ApiMethods.putAdminUrl(url, data);
  };

  static deleteDSAUrl = (portfolioId, propertyKey) => {
    const url = ENDPOINTS.DELETE_DSA_URL(portfolioId, propertyKey);
    return ApiMethods.deleteAdminUrl(url);
  };

  static postAddMoreReports = (
    fileData,
    appId,
    submissionId,
    reportComment = "",
    nextReviewStatus = null
  ) => {
    const url = ENDPOINTS.POST_ADD_MORE_REPORTS(
      appId,
      submissionId,
      reportComment,
      nextReviewStatus
    );
    return ApiMethods.apiRequestFormData("POST", url, fileData);
  };

  static getWelcomeWizardCampaigns = (portfolioId) => {
    const url = ENDPOINTS.GET_WELCOME_WIZARD_CAMPAIGN(portfolioId);
    return ApiMethods.getAdminUrl(url);
  };

  static postAppWelcomeWizardCampaign = (portfolioId, campaignData) => {
    const url = ENDPOINTS.POST_GET_APPS_WELCOME_WIZARD_CAMPAIGN(portfolioId);
    return ApiMethods.postAdminUrl(url, campaignData);
  };

  static getClusterNumberIdp = (portfolioId) => {
    const url = ENDPOINTS.GET_CLUSTER_NUMBER_IDP(portfolioId);
    return ApiMethods.getAdminUrl(url);
  };
  static getMMYPortfolio = (portfolioId) => {
    const url = ENDPOINTS.GET_MMY_FOR_PORTFOLIO(portfolioId);
    return ApiMethods.getAdminUrl(url);
  };

  static postCreateWelcomeWizardCampaigns = (portfolioId, campaignData) => {
    const url = ENDPOINTS.POST_CREATE_CAMPAIGN(portfolioId);
    return ApiMethods.postAdminUrl(url, campaignData);
  };

  static getAppsForCampaign = (portfolioId, campaignName) => {
    const url = ENDPOINTS.GET_APPS_FOR_CAMPAIGN(portfolioId, campaignName);
    return ApiMethods.getAdminUrl(url);
  };

  static getFlexMetaDataLabels = (portfolioId) => {
    const url = ENDPOINTS.GET_FLEX_META_DATA_LABELS(portfolioId);
    return ApiMethods.getAdminUrl(url);
  };

  static postFlexMetaDataLabels = (portfolioId, payload) => {
    const url = ENDPOINTS.POST_FLEX_META_DATA_LABELS(portfolioId);
    return ApiMethods.postAdminUrl(url, payload);
  };

  static deleteFlexMetaDataLabel = (portfolioId, labelId) => {
    const url = ENDPOINTS.DELETE_FLEX_META_DATA_LABEL(portfolioId, labelId);
    return ApiMethods.deleteAdminUrl(url);
  };

  static patchAddAppsWelcomeWizardCampaign = (
    portfolioId,
    campaignName,
    appData
  ) => {
    const url = ENDPOINTS.PATCH_ADD_APPS_WELCOME_WIZARD_CAMPAIGN(
      portfolioId,
      campaignName
    );
    return ApiMethods.patchAdminUrl(url, appData);
  };

  static reorderAppsForWelcomeWizardCampaign = (
    portfolioId,
    campaignName,
    appData
  ) => {
    const url = ENDPOINTS.GET_REORDER_APPS_FOR_CAMPAIGN(
      portfolioId,
      campaignName
    );
    return ApiMethods.putAdminUrl(url, appData);
  };

  static deleteAppFromWelcomeWizardCampaign = (
    portfolioId,
    campaignName,
    packageId
  ) => {
    const url = ENDPOINTS.DELETE_APP_WELCOME_WIZARD_CAMPAIGN(
      portfolioId,
      campaignName,
      packageId
    );
    return ApiMethods.deleteAdminUrl(url);
  };

  static deleteWelcomeWizardCampaign = (portfolioId, campaignName) => {
    const url = ENDPOINTS.DELETE_CAMPAIGN_APP_WELCOME_WIZARD(
      portfolioId,
      campaignName
    );
    return ApiMethods.deleteAdminUrl(url);
  };

  static getOEMAdminContentsMMY = (portfolioId) => {
    const url = ENDPOINTS.GET_OEM_ADMIN_CONTENTS_MMY(portfolioId);
    return ApiMethods.getAdminUrl(url);
  };

  static getOEMAdminECUTypeMappings = (portfolioId) => {
    const url = ENDPOINTS.GET_OEM_ADMIN_ECUTYPE_MAPPINGS_URL(portfolioId);
    return ApiMethods.getAdminUrl(url);
  };

  static putECUTYPEMapping = (portfolioId, data) => {
    const url = ENDPOINTS.PUT_DSA_URL(portfolioId)+"?updateEcuTypeMappings=true";
    return ApiMethods.putAdminUrlRaw(url, data);
  };

  static deleteECUTYPEMapping = (portfolioId, propertyKey) => {
    const url = ENDPOINTS.DELETE_OEM_ADMIN_ECUTYPE_MAPPINGS_URL(portfolioId, propertyKey);
    return ApiMethods.deleteAdminUrl(url);
  };

  static toggleMFA = (data) => {
    const url = ENDPOINTS.TOGGLE_MFA(data?.id);
    return ApiMethods.patch(url, data);
  };

  static putRecommendedAppsOrder = (portfolioId, data) => {
    const url = ENDPOINTS.PUT_REORDER_RECOMMENDED_APPS(portfolioId);
    return ApiMethods.putAdminUrl(url, data);
  }

  static searchDependencyApps = (portfolioId, searchText, locale) => {
    const url = ENDPOINTS.GET_DEPENDENCY_SEARCH(portfolioId, searchText, locale);
    return ApiMethods.getAdminUrl(url);
  };

  static updatePortfolioContentAppDependency = (
    portfolioId,
    contentId,
    appDependencyInfo
  ) => {
    const url = ENDPOINTS.PATCH_PORTFOLIO_CONTENTS_DETAILS(
      portfolioId,
      contentId
    );
    const formData = new FormData();
    formData.append("appDependencyInfo", JSON.stringify(appDependencyInfo));
    return ApiMethods.patchAdminUrl(url, formData);
  };

}

export default APIManager;
